import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  VStack,
  HStack,
  Text,
  Textarea,
  IconButton,
  Input,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  Container,
  Heading,
  Image,
  Center,
  useToast,
  AlertDialog,
  AlertDialogBody,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogContent,
  AlertDialogOverlay,
  useDisclosure,
} from '@chakra-ui/react';
import { CameraIcon } from './icons';
import { DeleteIcon, InfoIcon, TimeIcon } from '@chakra-ui/icons';
import LiquidGlassButton from './ui/LiquidGlassButton';
import { useLanguage } from '../i18n/LanguageContext';

interface ParkingRecord {
  data: {
    selectedColor: string | null;
    selectedBasement: string | null;
    lotNumber: string;
    notes: string;
    image: string | null;
  };
  timestamp: number;
}

interface ParkingDashboardProps {
  currentProfile: string;
  onProfileChange: (profile: string) => void;
}

type ColorOption = 'yellow' | 'orange' | 'red' | 'blue' | 'green' | 'teal' | 'purple' | 'pink';

const COLOR_LABELS: Record<ColorOption, string> = {
  yellow: 'L1', orange: 'L2', red: 'L3', blue: 'L4', green: 'L5',
  teal: 'L6', purple: 'L7', pink: 'L8',
};
const COLOR_HEX: Record<ColorOption, string> = {
  yellow: '#FBBF24', orange: '#FB923C', red: '#F87171', blue: '#60A5FA', green: '#4ADE80',
  teal: '#2DD4BF', purple: '#A78BFA', pink: '#F472B6',
};

const basementOptions = ['B1', 'B2', 'B3'];
const HISTORY_KEY = 'myCarParkedWhereHistory_';

// ── Shared glass card props ────────────────────────────────────────────────────
// Dark-tinted glass so cards read clearly against the bright roadtrip background
const glassCard = {
  bg: 'rgba(30,35,50,0.55)',
  backdropFilter: 'blur(40px) saturate(180%)',
  border: '1px solid rgba(255,255,255,0.08)',
  borderRadius: '22px',
  boxShadow: '0 4px 30px rgba(0,0,0,0.35), 0 1px 0 inset rgba(255,255,255,0.07)',
  sx: { WebkitBackdropFilter: 'blur(40px) saturate(180%)', willChange: 'transform' },
};

const glassCardHi = {
  bg: 'transparent',
  border: '1.5px solid rgba(255,255,255,0.45)',
  borderRadius: '12px',
};

// ── Hairline section divider ───────────────────────────────────────────────────
const SectionDivider = () => (
  <Box h="1px" bg="linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.07) 20%, rgba(255,255,255,0.07) 80%, transparent 100%)" />
);

// ── Level tab card ─────────────────────────────────────────────────────────────
const LevelTab = ({
  label, color, isActive, onClick,
}: { label: string; color: string; isActive: boolean; onClick: () => void }) => (
  <Box
    as="button"
    onClick={onClick}
    flex="1 0 60px"
    minW="60px"
    h="68px"
    borderRadius="16px"
    display="flex"
    flexDirection="column"
    alignItems="center"
    justifyContent="center"
    gap="7px"
    flexShrink={1}
    cursor="pointer"
    position="relative"
    overflow="visible"
    transition="border-color 0.22s, background 0.22s, box-shadow 0.22s, transform 0.18s cubic-bezier(0.34,1.56,0.64,1)"
    border="1.5px solid"
    borderColor={isActive ? color : 'rgba(255,255,255,0.18)'}
    bg={isActive
      ? [
          `radial-gradient(ellipse at 50% 20%, ${color}38 0%, ${color}12 60%, transparent 100%)`,
          `radial-gradient(ellipse at 28% 18%, rgba(255,255,255,0.18) 0%, rgba(255,255,255,0.04) 36%, transparent 58%)`,
          `rgba(255,255,255,0.07)`,
        ].join(', ')
      : [
          `radial-gradient(ellipse at 28% 18%, rgba(255,255,255,0.14) 0%, rgba(255,255,255,0.03) 36%, transparent 58%)`,
          `rgba(255,255,255,0.06)`,
        ].join(', ')}
    backdropFilter="blur(22px) saturate(200%)"
    boxShadow={isActive
      ? `0 0 32px ${color}44, 0 6px 20px rgba(0,0,0,0.4), inset 0 1.5px 0 rgba(255,255,255,0.35), inset 0 -1px 0 rgba(0,0,0,0.18), inset 1px 0 rgba(255,255,255,0.1)`
      : '0 4px 16px rgba(0,0,0,0.3), inset 0 1.5px 0 rgba(255,255,255,0.22), inset 0 -1px 0 rgba(0,0,0,0.12)'}
    transform={isActive ? 'scale(1.07)' : 'scale(1)'}
    _hover={{
      borderColor: isActive ? color : `rgba(255,255,255,0.32)`,
      boxShadow: isActive
        ? `0 0 38px ${color}55, 0 6px 20px rgba(0,0,0,0.4), inset 0 1.5px 0 rgba(255,255,255,0.4), inset 0 -1px 0 rgba(0,0,0,0.18)`
        : '0 4px 20px rgba(0,0,0,0.35), inset 0 1.5px 0 rgba(255,255,255,0.3), inset 0 -1px 0 rgba(0,0,0,0.12)',
      transform: isActive ? 'scale(1.09)' : 'scale(1.05)',
    }}
    _active={{ transform: 'scale(0.95)', transition: 'transform 0.1s ease' }}
    sx={{ WebkitBackdropFilter: 'blur(22px) saturate(200%)' }}
  >
    {/* Top specular — curved glass catching light */}
    <Box
      position="absolute" top={0} left={0} right={0} h="50%"
      borderRadius="18px 18px 0 0"
      bg="radial-gradient(ellipse at 40% 0%, rgba(255,255,255,0.22) 0%, transparent 65%)"
      pointerEvents="none"
    />
    {/* Bottom caustic */}
    <Box
      position="absolute" bottom={0} left={0} right={0} h="30%"
      borderRadius="0 0 18px 18px"
      bg="radial-gradient(ellipse at 60% 100%, rgba(255,255,255,0.04) 0%, transparent 60%)"
      pointerEvents="none"
    />
    {/* Color glow dot */}
    <Box
      w={isActive ? "13px" : "9px"} h={isActive ? "13px" : "9px"}
      borderRadius="full"
      bg={color}
      opacity={isActive ? 1 : 0.55}
      boxShadow={isActive ? `0 0 10px ${color}, 0 0 22px ${color}77` : 'none'}
      transition="all 0.22s"
      flexShrink={0}
      zIndex={1}
    />
    {/* Level label */}
    <Text
      fontSize="22px"
      fontWeight="900"
      color="white"
      letterSpacing="-0.03em"
      lineHeight={1}
      transition="text-shadow 0.22s"
      textShadow={
        isActive
          ? `0 0 14px ${color}88, 0 1px 6px rgba(0,0,0,0.85)`
          : '0 1px 6px rgba(0,0,0,0.85)'
      }
      zIndex={1}
    >
      {label}
    </Text>
  </Box>
);

const ParkingDashboard: React.FC<ParkingDashboardProps> = ({ currentProfile }) => {
  const { t } = useLanguage();
  const [selectedColor, setSelectedColor] = useState<ColorOption | null>(null);
  const [selectedBasement, setSelectedBasement] = useState<string | null>(null);
  const [lotNumber, setLotNumber] = useState('');
  const [notes, setNotes] = useState('');
  const [records, setRecords] = useState<ParkingRecord[]>([]);
  const [currentLocation, setCurrentLocation] = useState<ParkingRecord | null>(null);
  const [isCameraOpen, setIsCameraOpen] = useState(false);
  const [videoStream, setVideoStream] = useState<MediaStream | null>(null);
  const [capturedImage, setCapturedImage] = useState<string | null>(null);
  const [isImageModalOpen, setIsImageModalOpen] = useState(false);
  const [expandedImage, setExpandedImage] = useState<string | null>(null);
  const [isExpandedImageModalOpen, setIsExpandedImageModalOpen] = useState(false);

  const videoRef = useRef<HTMLVideoElement | null>(null);
  const cancelRef = useRef<HTMLButtonElement>(null);
  const toast = useToast();
  const { isOpen: isClearAllOpen, onOpen: onClearAllOpen, onClose: onClearAllClose } = useDisclosure();
  const { isOpen: isDeletePinOpen, onOpen: onDeletePinOpen, onClose: onDeletePinClose } = useDisclosure();

  const colorOptions: ColorOption[] = ['yellow', 'orange', 'red', 'blue', 'green'];

  const hKey = (p: string) => `${HISTORY_KEY}${p}`;
  const cKey = (p: string) => `currentLocation_${p}`;

  useEffect(() => {
    const stored = localStorage.getItem(hKey(currentProfile));
    setRecords(stored ? JSON.parse(stored) : []);
    const cur = localStorage.getItem(cKey(currentProfile));
    setCurrentLocation(cur ? JSON.parse(cur) : null);
    setSelectedColor(null); setSelectedBasement(null);
    setLotNumber(''); setNotes(''); setCapturedImage(null);
  }, [currentProfile]);

  const canSave = () =>
    selectedColor !== null || selectedBasement !== null || lotNumber.trim() !== '' || notes.trim() !== '';

  // ── Toast ──────────────────────────────────────────────────────────────────
  const GlassToast = ({ emoji, title, sub }: { emoji: string; title: string; sub?: string }) => (
    <Box
      bg="rgba(8,11,20,0.92)" backdropFilter="blur(28px)"
      px={5} py={4}
      borderRadius="18px"
      display="inline-flex"
      mx="auto"
      maxW="92vw"
      border="1px solid rgba(255,255,255,0.12)"
      boxShadow="0 12px 40px rgba(0,0,0,0.6), inset 0 1px 0 rgba(255,255,255,0.1)"
      sx={{ WebkitBackdropFilter: 'blur(28px)' }}
    >
      <HStack spacing={3} align="center">
        {emoji && (
          <Box w="36px" h="36px" borderRadius="10px" bg="rgba(255,255,255,0.14)"
            border="1px solid rgba(255,255,255,0.22)"
            display="flex" alignItems="center" justifyContent="center" fontSize="md" flexShrink={0}>
            {emoji}
          </Box>
        )}
        <VStack align="start" spacing={sub ? 0.5 : 0} flex={1} minW={0}>
          <Text fontWeight="700" fontSize="sm" whiteSpace="nowrap">{title}</Text>
          {sub && <Text fontSize="xs" color="rgba(255,255,255,0.45)" whiteSpace="nowrap">{sub}</Text>}
        </VStack>
      </HStack>
    </Box>
  );
  const notify = (emoji: string, title: string, sub?: string) =>
    toast({ position: 'top', duration: 2600, render: () => <GlassToast emoji={emoji} title={title} sub={sub} /> });

  // ── Save ───────────────────────────────────────────────────────────────────
  const handleSave = () => {
    if (!canSave()) { notify('', t('addDetails'), t('selectLevelPrompt')); return; }
    const entry: ParkingRecord = {
      data: { selectedColor, selectedBasement, lotNumber, notes, image: capturedImage },
      timestamp: Date.now(),
    };
    localStorage.setItem(cKey(currentProfile), JSON.stringify(entry));
    setCurrentLocation(entry);
    const hist = [entry, ...records];
    localStorage.setItem(hKey(currentProfile), JSON.stringify(hist));
    setRecords(hist);
    setSelectedColor(null); setSelectedBasement(null);
    setLotNumber(''); setNotes(''); setCapturedImage(null);
    if (videoStream) { videoStream.getTracks().forEach(t => t.stop()); setVideoStream(null); }
    notify('', t('locationSaved'), '');
  };

  // ── Camera ─────────────────────────────────────────────────────────────────
  const openCamera = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: 'environment' } });
      setVideoStream(stream); setIsCameraOpen(true);
      setTimeout(() => { if (videoRef.current) videoRef.current.srcObject = stream; }, 100);
    } catch { notify('', t('cameraUnavailable')); }
  };
  const handleCapture = () => {
    if (!videoRef.current) return;
    const c = document.createElement('canvas');
    c.width = videoRef.current.videoWidth; c.height = videoRef.current.videoHeight;
    c.getContext('2d')?.drawImage(videoRef.current, 0, 0);
    setCapturedImage(c.toDataURL('image/jpeg', 0.8));
    if (videoStream) { videoStream.getTracks().forEach(t => t.stop()); setVideoStream(null); }
  };
  const closeCamera = () => {
    setIsCameraOpen(false);
    if (videoStream) { videoStream.getTracks().forEach(t => t.stop()); setVideoStream(null); }
  };

  const levelOf = (r: ParkingRecord) =>
    r.data.selectedColor ? COLOR_LABELS[r.data.selectedColor as ColorOption] : (r.data.selectedBasement || '—');
  const colorOf = (r: ParkingRecord): string =>
    r.data.selectedColor ? COLOR_HEX[r.data.selectedColor as ColorOption] : '#e0e4ea';

  return (
    <Container maxW="100%" px={0} py={0}>
      <VStack spacing={0} align="stretch">

        {/* ── Current Location ─────────────────────────────────────────── */}
        {currentLocation && (
          <Box px={3} mt={0} mb={3}>
            <Box {...glassCardHi} px={5} pt={1.5} pb={3} position="relative" overflow="hidden">
              {/* Color blob — softer, larger ambient glow */}
              <Box position="absolute" top="-30px" right="-20px" w="160px" h="160px"
                borderRadius="full" bg={colorOf(currentLocation)} opacity={0.05}
                filter="blur(50px)" pointerEvents="none" />
              <Box position="absolute" bottom="-20px" left="-10px" w="100px" h="100px"
                borderRadius="full" bg={colorOf(currentLocation)} opacity={0.03}
                filter="blur(40px)" pointerEvents="none" />

              <HStack justify="space-between" align="center" mb={0}>
                <HStack spacing={2}>
                  <Box w="7px" h="7px" borderRadius="full" bg="rgba(74,222,128,0.9)"
                    boxShadow="0 0 10px rgba(74,222,128,0.6), 0 0 20px rgba(74,222,128,0.25)" />
                  <Text fontSize="11px" fontWeight="700" letterSpacing="0.08em"
                    textTransform="uppercase" color="white">
                    {t('pinnedLocations')}
                  </Text>
                </HStack>
                <IconButton
                  aria-label="Clear" icon={<DeleteIcon boxSize={5} />} size="md" variant="ghost"
                  color="rgba(255,255,255,0.45)" borderRadius="10px"
                  minW="40px" minH="40px"
                  _hover={{ bg: 'rgba(248,113,113,0.15)', color: 'rgba(248,113,113,0.9)' }}
                  _active={{ bg: 'rgba(248,113,113,0.25)' }}
                  onClick={onDeletePinOpen}
                />
              </HStack>

              <HStack spacing={4} align="center">
                {/* Level badge — glassmorphic with colored tint */}
                <Box
                  w="62px" h="62px" borderRadius="full" flexShrink={0}
                  position="relative"
                  bg={`radial-gradient(circle at 40% 35%, ${colorOf(currentLocation)}30 0%, ${colorOf(currentLocation)}12 50%, transparent 80%), rgba(255,255,255,0.08)`}
                  backdropFilter="blur(20px) saturate(180%)"
                  border={`1.5px solid ${colorOf(currentLocation)}50`}
                  display="flex" alignItems="center" justifyContent="center"
                  boxShadow={`0 0 24px ${colorOf(currentLocation)}25, 0 4px 16px rgba(0,0,0,0.25), inset 0 1px 0 rgba(255,255,255,0.15)`}
                  sx={{ WebkitBackdropFilter: 'blur(20px) saturate(180%)' }}
                >
                  <Text fontSize="22px" fontWeight="900" color="white" letterSpacing="-0.03em"
                    textShadow={`0 0 16px ${colorOf(currentLocation)}, 0 0 6px rgba(255,255,255,0.5)`}>
                    {levelOf(currentLocation)}
                  </Text>
                </Box>

                <VStack align="start" spacing={1} flex={1} minW={0}>
                  {currentLocation.data.lotNumber && (
                    <Text fontSize="20px" fontWeight="800" color="white" letterSpacing="-0.02em" noOfLines={1}>
                      {t('lot')} {currentLocation.data.lotNumber}
                    </Text>
                  )}
                  {currentLocation.data.notes && (
                    <Text fontSize="14px" color="rgba(255,255,255,0.7)" noOfLines={2} lineHeight="1.4">{currentLocation.data.notes}</Text>
                  )}
                  <HStack spacing={1.5} mt={1}>
                    <TimeIcon color="rgba(255,255,255,0.45)" boxSize="10px" />
                    <Text fontSize="12px" color="rgba(255,255,255,0.55)" fontWeight="500">
                      {new Date(currentLocation.timestamp).toLocaleString([], {
                        month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit'
                      })}
                    </Text>
                  </HStack>
                </VStack>
              </HStack>

              {currentLocation.data.image && (
                <Image src={currentLocation.data.image} alt="parking spot" mt={3}
                  borderRadius="12px" maxH="90px" w="100%" objectFit="cover"
                  cursor="pointer" border="1px solid rgba(255,255,255,0.07)"
                  onClick={() => { setExpandedImage(currentLocation.data.image!); setIsExpandedImageModalOpen(true); }}
                  _hover={{ opacity: 0.82 }} transition="opacity 0.2s" />
              )}
            </Box>
          </Box>
        )}

        {/* ── Floor / Level Selector ────────────────────────────────── */}
        <Box px={3} mb={3}>
          <Text
            fontSize="14px" fontWeight="700" color="white"
            textShadow="0 1px 8px rgba(0,0,0,0.9)" mb={2} letterSpacing="-0.01em"
          >
            {t('whereDidYouPark')}
          </Text>
          <VStack spacing={2} align="stretch" w="100%">
            {/* Row 1 — Levels (scrollable) */}
            <Box
              overflowX="auto"
              overflowY="visible"
              mx={-1}
              px={1}
              py={1}
              sx={{
                '&::-webkit-scrollbar': { display: 'none' },
                scrollbarWidth: 'none',
                WebkitOverflowScrolling: 'touch',
              }}
            >
              <HStack spacing={1.5} w="max-content" minW="100%">
                {colorOptions.map((color) => (
                  <LevelTab
                    key={color}
                    label={COLOR_LABELS[color]}
                    color={COLOR_HEX[color]}
                    isActive={selectedColor === color}
                    onClick={() => { setSelectedColor(color); setSelectedBasement(null); }}
                  />
                ))}
              </HStack>
            </Box>
            {/* Row 2 — Basements (scrollable) */}
            <Box
              overflowX="auto"
              overflowY="visible"
              mx={-1}
              px={1}
              py={1}
              sx={{
                '&::-webkit-scrollbar': { display: 'none' },
                scrollbarWidth: 'none',
                WebkitOverflowScrolling: 'touch',
              }}
            >
              <HStack spacing={1.5} w="max-content" minW="100%">
                {basementOptions.map((b) => (
                  <LevelTab
                    key={b}
                    label={b}
                    color="#9CA3AF"
                    isActive={selectedBasement === b}
                    onClick={() => { setSelectedBasement(b); setSelectedColor(null); }}
                  />
                ))}
              </HStack>
            </Box>
          </VStack>
        </Box>

        {/* ── Entry form card ───────────────────────────────────────────── */}
        <Box px={3} mb={3}>
          <Box {...glassCard} overflow="hidden">

            {/* Inputs */}
            <VStack spacing={0} align="stretch">
              {/* Lot / Space */}
              <Box px={2} pt={3} pb={2}>
                <Input
                  placeholder={t('lotPlaceholder')}
                  value={lotNumber}
                  onChange={(e) => setLotNumber(e.target.value)}
                  size="lg"
                  fontSize="16px"
                  fontWeight="500"
                  h="52px"
                  w="100%"
                  bg="rgba(0,0,0,0.25)"
                  border="1px solid rgba(255,255,255,0.1)"
                  borderRadius="12px"
                  _placeholder={{ color: 'rgba(255,255,255,0.28)', fontSize: '16px', fontWeight: '400' }}
                  _hover={{ bg: 'rgba(0,0,0,0.3)', borderColor: 'rgba(255,255,255,0.16)' }}
                  _focus={{ bg: 'rgba(0,0,0,0.3)', borderColor: 'rgba(255,255,255,0.3)', boxShadow: '0 0 0 3px rgba(255,255,255,0.06)' }}
                />
              </Box>

              <Box px={2} pb={3}>
                <Textarea
                  placeholder={t('notesPlaceholder')}
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  rows={3} resize="none"
                  fontSize="16px"
                  fontWeight="500"
                  w="100%"
                  bg="rgba(0,0,0,0.25)"
                  border="1px solid rgba(255,255,255,0.1)"
                  borderRadius="12px"
                  _placeholder={{ color: 'rgba(255,255,255,0.28)', fontSize: '16px' }}
                  _hover={{ bg: 'rgba(0,0,0,0.3)', borderColor: 'rgba(255,255,255,0.16)' }}
                  _focus={{ bg: 'rgba(0,0,0,0.3)', borderColor: 'rgba(255,255,255,0.3)', boxShadow: '0 0 0 3px rgba(255,255,255,0.06)' }}
                />
              </Box>

              {/* Photo preview */}
              {capturedImage && (
                <>
                  <SectionDivider />
                  <Box px={5} pb={4} pt={3}>
                    <Box borderRadius="14px" overflow="hidden" position="relative">
                      <Image
                        src={capturedImage} alt="Captured" maxH="130px" w="100%" objectFit="cover"
                        cursor="pointer" onClick={() => setIsImageModalOpen(true)}
                        _hover={{ opacity: 0.85 }} transition="opacity 0.2s"
                      />
                      <HStack
                        position="absolute" bottom={0} left={0} right={0}
                        px={3} py={2} justify="space-between"
                        bg="linear-gradient(0deg, rgba(0,0,0,0.72) 0%, transparent 100%)"
                      >
                        <Text fontSize="11px" color="rgba(255,255,255,0.65)" fontWeight="600">
                          📸 {t('photoAttached')}
                        </Text>
                        <IconButton
                          aria-label="Remove" icon={<DeleteIcon />} size="xs"
                          variant="ghost" color="rgba(255,255,255,0.5)"
                          _hover={{ color: 'white' }} onClick={() => setCapturedImage(null)}
                        />
                      </HStack>
                    </Box>
                  </Box>
                </>
              )}
            </VStack>

            <SectionDivider />

            {/* Action row */}
            <HStack spacing={3} px={4} py={4} align="center">
              {/* Camera button */}
              <LiquidGlassButton
                shape="circle" circleSize="64px"
                onClick={openCamera} flexShrink={0}
                transform="none" _hover={{ transform: 'scale(1.06)' }}
              >
                <CameraIcon color="rgba(255,255,255,0.85)" boxSize={7} />
              </LiquidGlassButton>

              {/* Save CTA — same LiquidGlass style as Camera button */}
              <LiquidGlassButton
                shape="pill"
                flex={1} minH="64px"
                onClick={canSave() ? handleSave : undefined}
                cursor={canSave() ? 'pointer' : 'default'}
                opacity={canSave() ? 1 : 0.5}
                transform="none"
                _hover={canSave() ? { transform: 'scale(1.03)' } : {}}
              >
                <Text
                  fontSize="17px" fontWeight="800" letterSpacing="0.01em"
                  color={canSave() ? 'rgba(255,255,255,0.9)' : 'rgba(255,255,255,0.35)'}
                >
                  {t('saveLocation')}
                </Text>
              </LiquidGlassButton>
            </HStack>
          </Box>
        </Box>

        {/* ── Recent Records ───────────────────────────────────────────── */}
        <Box px={3} mb={2}>
          <Box
            p={3}
            border="1.5px solid rgba(255,255,255,0.25)"
            borderRadius="12px"
          >
          {/* Section header */}
          <HStack justify="space-between" align="center" mb={3}>
            <HStack spacing={2.5}>
              <Heading fontSize="16px" fontWeight="800" color="white" letterSpacing="-0.02em">
                {t('recentRecords')}
              </Heading>
              <Box px={2} py={0.5} borderRadius="full"
                bg="rgba(255,255,255,0.1)" border="1px solid rgba(255,255,255,0.2)">
                <Text fontSize="10px" fontWeight="800" color="#e0e4ea">{records.length}</Text>
              </Box>
            </HStack>
            {records.length > 0 && (
              <Box
                as="button"
                onClick={onClearAllOpen}
                px={3} py={1.5}
                borderRadius="8px"
                bg="rgba(248,113,113,0.15)"
                border="1px solid rgba(248,113,113,0.3)"
                cursor="pointer"
                transition="all 0.15s"
                _hover={{ bg: 'rgba(248,113,113,0.25)' }}
                _active={{ transform: 'scale(0.95)' }}
              >
                <Text fontSize="11px" fontWeight="700" color="#F87171">{t('clearAll')}</Text>
              </Box>
            )}
          </HStack>

          {records.length === 0 ? (
            <Box bg="rgba(140,150,165,0.12)" border="1px solid rgba(255,255,255,0.08)" borderRadius="22px" py={10}>
              <Center>
                <VStack spacing={3}>
                  <Box w="48px" h="48px" borderRadius="16px"
                    bg="rgba(255,255,255,0.06)" border="1px solid rgba(255,255,255,0.08)"
                    display="flex" alignItems="center" justifyContent="center">
                    <InfoIcon color="rgba(255,255,255,0.25)" boxSize={5} />
                  </Box>
                  <Text color="rgba(255,255,255,0.55)" fontSize="sm" fontWeight="500" textAlign="center">
                    {t('noRecordsYet')}
                  </Text>
                </VStack>
              </Center>
            </Box>
          ) : (
            <VStack spacing={2.5} align="stretch" maxH="360px" overflowY="auto">
              {records.slice(0, 5).map((record, i) => {
                const lc = colorOf(record);
                return (
                  <Box
                    key={i}
                    bg="rgba(140,150,165,0.12)"
                    backdropFilter="blur(40px) saturate(180%)"
                    border="1px solid rgba(255,255,255,0.08)"
                    borderRadius="0 22px 22px 0"
                    boxShadow="0 2px 12px rgba(0,0,0,0.2)"
                    sx={{ WebkitBackdropFilter: 'blur(40px) saturate(180%)' }}
                    p={4}
                    borderLeft="2.5px solid"
                    borderLeftColor={lc}
                    _hover={{ bg: 'rgba(140,150,165,0.18)' }}
                    transition="background 0.15s"
                  >
                    <HStack justify="space-between" align="start">
                      <HStack spacing={3} flex={1}>
                        <LiquidGlassButton
                          shape="circle" circleSize="36px"
                          isActive activeColor={lc} cursor="default" flexShrink={0}
                        >
                          <Text fontSize="10px" fontWeight="900" color={lc}>{levelOf(record)}</Text>
                        </LiquidGlassButton>

                        <VStack align="start" spacing={0.5} flex={1} minW={0}>
                          {record.data.lotNumber && (
                            <Text fontSize="sm" fontWeight="700" color="white" noOfLines={1}>
                              {t('lot')} {record.data.lotNumber}
                            </Text>
                          )}
                          {record.data.notes && (
                            <Text fontSize="xs" color="rgba(255,255,255,0.65)" noOfLines={1}>
                              {record.data.notes}
                            </Text>
                          )}
                          <Text fontSize="10px" color="rgba(255,255,255,0.5)">
                            {new Date(record.timestamp).toLocaleDateString([], { month: 'short', day: 'numeric' })}
                            {' · '}
                            {new Date(record.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                          </Text>
                        </VStack>
                      </HStack>

                      <IconButton
                        aria-label="Delete"
                        icon={<DeleteIcon boxSize={4} />}
                        size="sm"
                        variant="ghost"
                        color="rgba(255,255,255,0.22)"
                        borderRadius="xl"
                        minW="40px" minH="40px"
                        _hover={{ color: 'rgba(248,113,113,0.85)', bg: 'rgba(248,113,113,0.12)' }}
                        _active={{ bg: 'rgba(248,113,113,0.2)' }}
                        onClick={() => {
                          const u = records.filter((_, idx) => idx !== i);
                          setRecords(u);
                          u.length > 0
                            ? localStorage.setItem(hKey(currentProfile), JSON.stringify(u))
                            : localStorage.removeItem(hKey(currentProfile));
                        }}
                      />
                    </HStack>

                    {record.data.image && (
                      <Image src={record.data.image} alt="record" mt={2.5}
                        borderRadius="10px" maxH="65px" w="100%" objectFit="cover"
                        cursor="pointer" border="1px solid rgba(255,255,255,0.06)"
                        onClick={() => { setExpandedImage(record.data.image!); setIsExpandedImageModalOpen(true); }}
                        _hover={{ opacity: 0.78 }} transition="opacity 0.2s" />
                    )}
                  </Box>
                );
              })}
            </VStack>
          )}
          </Box>
        </Box>
      </VStack>

      {/* ── Modals ────────────────────────────────────────────────────── */}

      {/* Photo preview */}
      <Modal isOpen={isImageModalOpen} onClose={() => setIsImageModalOpen(false)} size="full">
        <ModalOverlay bg="rgba(0,0,0,0.94)" backdropFilter="blur(10px)" />
        <ModalContent bg="transparent" m={0} boxShadow="none">
          <ModalHeader color="white" bg="rgba(6,9,18,0.82)" backdropFilter="blur(16px)"
            borderBottom="1px solid rgba(255,255,255,0.06)" fontSize="sm" fontWeight="700">
            {t('photoPreview')}
          </ModalHeader>
          <ModalCloseButton color="white" />
          <ModalBody display="flex" alignItems="center" justifyContent="center">
            {capturedImage && <Image src={capturedImage} alt="preview" maxW="100%" maxH="80vh" objectFit="contain" borderRadius="12px" />}
          </ModalBody>
        </ModalContent>
      </Modal>

      {/* Camera */}
      <Modal isOpen={isCameraOpen} onClose={closeCamera} size="full">
        <ModalOverlay bg="black" />
        <ModalContent bg="#000" m={0} h="100vh" display="flex" flexDirection="column"
          pt={{ base: 'env(safe-area-inset-top)', md: 0 }}
          pb={{ base: 'env(safe-area-inset-bottom)', md: 0 }}>
          <ModalHeader color="white" bg="rgba(0,0,0,0.6)" backdropFilter="blur(12px)"
            fontSize="sm" fontWeight="700" flexShrink={0} pt={{ base: 2, md: 6 }}>
            {t('takePhoto')}
          </ModalHeader>
          <ModalCloseButton color="white" top={{ base: 2, md: 6 }} />
          <ModalBody p={0} flex={1} display="flex" flexDirection="column" minH={0}>
            <Box flex={1} minH={0}>
              {!capturedImage
                ? <video ref={videoRef} autoPlay playsInline style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                : <Image src={capturedImage} alt="captured" w="100%" h="100%" objectFit="cover" />
              }
            </Box>
          </ModalBody>
          <ModalFooter bg="rgba(0,0,0,0.78)" backdropFilter="blur(16px)" flexShrink={0} py={6}
            pb={{ base: 'calc(env(safe-area-inset-bottom,0px) + 24px)', md: 6 }}>
            <HStack w="100%" justify="center" spacing={4}>
              {!capturedImage ? (
                <Box as="button" onClick={handleCapture} w="70px" h="70px" borderRadius="full"
                  cursor="pointer" display="flex" alignItems="center" justifyContent="center"
                  border="3px solid rgba(255,255,255,0.65)" bg="rgba(255,255,255,0.12)"
                  backdropFilter="blur(8px)"
                  boxShadow="0 0 0 1px rgba(255,255,255,0.2), 0 8px 32px rgba(0,0,0,0.5), inset 0 1px 0 rgba(255,255,255,0.3)"
                  transition="transform 0.12s" _hover={{ transform: 'scale(1.05)' }} _active={{ transform: 'scale(0.93)' }}>
                  <Box w="50px" h="50px" borderRadius="full" bg="rgba(255,255,255,0.88)"
                    boxShadow="inset 0 2px 4px rgba(0,0,0,0.15)" />
                </Box>
              ) : (
                <HStack spacing={3}>
                  <LiquidGlassButton shape="pill" minH="48px" px={8}
                    onClick={() => { setCapturedImage(null); openCamera(); }}>
                    <Text fontSize="sm" fontWeight="700" color="rgba(255,255,255,0.7)">{t('retake')}</Text>
                  </LiquidGlassButton>
                  <LiquidGlassButton shape="pill" minH="48px" px={8} isActive activeColor="#e0e4ea" onClick={closeCamera}>
                    <Text fontSize="sm" fontWeight="700" color="#e0e4ea">{t('usePhoto')}</Text>
                  </LiquidGlassButton>
                </HStack>
              )}
            </HStack>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Expanded image */}
      <Modal isOpen={isExpandedImageModalOpen}
        onClose={() => { setExpandedImage(null); setIsExpandedImageModalOpen(false); }} size="full">
        <ModalOverlay bg="rgba(0,0,0,0.96)" backdropFilter="blur(14px)" />
        <ModalContent bg="transparent" m={0} boxShadow="none">
          <ModalHeader color="white" textAlign="center" bg="rgba(6,9,18,0.75)"
            backdropFilter="blur(16px)" fontSize="sm" fontWeight="700">{t('photo')}</ModalHeader>
          <ModalCloseButton color="white" />
          <ModalBody display="flex" alignItems="center" justifyContent="center" minH="calc(100vh - 120px)">
            {expandedImage && <Image src={expandedImage} alt="expanded"
              maxW="95vw" maxH="80vh" objectFit="contain" borderRadius="16px"
              boxShadow="0 24px 64px rgba(0,0,0,0.7)" />}
          </ModalBody>
          <ModalFooter justifyContent="center" bg="rgba(6,9,18,0.75)" backdropFilter="blur(16px)">
            <LiquidGlassButton shape="pill" minH="44px" px={8}
              onClick={() => { setExpandedImage(null); setIsExpandedImageModalOpen(false); }}>
              <Text fontSize="sm" fontWeight="600" color="rgba(255,255,255,0.65)">{t('close')}</Text>
            </LiquidGlassButton>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Delete pinned location confirmation */}
      <AlertDialog isOpen={isDeletePinOpen} leastDestructiveRef={cancelRef} onClose={onDeletePinClose}>
        <AlertDialogOverlay bg="rgba(0,0,0,0.85)" backdropFilter="blur(16px)">
          <AlertDialogContent
            bg="rgba(8,11,20,0.94)" backdropFilter="blur(32px)"
            border="1px solid rgba(255,255,255,0.1)" color="white"
            mx={4} borderRadius="24px"
            boxShadow="0 24px 64px rgba(0,0,0,0.65), inset 0 1.5px 0 rgba(255,255,255,0.1)"
            sx={{ WebkitBackdropFilter: 'blur(32px)' }}
          >
            <AlertDialogHeader fontSize="md" fontWeight="800" letterSpacing="-0.02em">
              {t('deletePinnedLocation')}
            </AlertDialogHeader>
            <AlertDialogBody color="rgba(255,255,255,0.5)" fontSize="sm" lineHeight="1.6">
              {t('removePinnedBody')}{' '}
              <Text as="span" fontWeight="700" color="white">{currentProfile}</Text>?
            </AlertDialogBody>
            <AlertDialogFooter gap={2.5}>
              <LiquidGlassButton shape="pill" minH="40px" px={5} ref={cancelRef as any} onClick={onDeletePinClose}>
                <Text fontSize="sm" fontWeight="600" color="rgba(255,255,255,0.5)">{t('cancel')}</Text>
              </LiquidGlassButton>
              <LiquidGlassButton shape="pill" minH="40px" px={5} isActive activeColor="#F87171"
                onClick={() => {
                  localStorage.removeItem(cKey(currentProfile));
                  setCurrentLocation(null); onDeletePinClose(); notify('', t('locationRemoved'));
                }}>
                <Text fontSize="sm" fontWeight="700" color="#F87171">{t('delete')}</Text>
              </LiquidGlassButton>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>

      {/* Clear all */}
      <AlertDialog isOpen={isClearAllOpen} leastDestructiveRef={cancelRef} onClose={onClearAllClose}>
        <AlertDialogOverlay bg="rgba(0,0,0,0.85)" backdropFilter="blur(16px)">
          <AlertDialogContent
            bg="rgba(8,11,20,0.94)" backdropFilter="blur(32px)"
            border="1px solid rgba(255,255,255,0.1)" color="white"
            mx={4} borderRadius="24px"
            boxShadow="0 24px 64px rgba(0,0,0,0.65), inset 0 1.5px 0 rgba(255,255,255,0.1)"
            sx={{ WebkitBackdropFilter: 'blur(32px)' }}
          >
            <AlertDialogHeader fontSize="md" fontWeight="800" letterSpacing="-0.02em">
              {t('clearAllRecords')}
            </AlertDialogHeader>
            <AlertDialogBody color="rgba(255,255,255,0.5)" fontSize="sm" lineHeight="1.6">
              {t('permanentlyDelete')}{' '}
              <Text as="span" fontWeight="700" color="white">{currentProfile}</Text>. {t('cannotBeUndone')}
            </AlertDialogBody>
            <AlertDialogFooter gap={2.5}>
              <LiquidGlassButton shape="pill" minH="40px" px={5} ref={cancelRef as any} onClick={onClearAllClose}>
                <Text fontSize="sm" fontWeight="600" color="rgba(255,255,255,0.5)">{t('cancel')}</Text>
              </LiquidGlassButton>
              <LiquidGlassButton shape="pill" minH="40px" px={5} isActive activeColor="#F87171"
                onClick={() => {
                  localStorage.removeItem(hKey(currentProfile));
                  setRecords([]); onClearAllClose(); notify('', t('recordsCleared'));
                }}>
                <Text fontSize="sm" fontWeight="700" color="#F87171">{t('deleteAll')}</Text>
              </LiquidGlassButton>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </Container>
  );
};

export default ParkingDashboard;
